import { today } from '@/lib/format';
import type {
  Inspection,
  Ncr,
  PurchaseOrder,
  QualityTestReport,
  SafetyCorrectiveAction,
  SafetyInspection,
  ShopDrawing,
  Tbm,
} from '@/lib/types';

/*
  「오늘 답해야 닫히는 것」을 세는 규칙 — 한 곳에만 둔다.

  홈 위젯의 머리 배지와 사이드바 「오늘」 묶음의 건수가 **같은 숫자**여야 한다. 두 곳이 각자
  세면 언젠가 한쪽만 규칙이 바뀌어 위젯은 3 인데 메뉴는 2 인 화면이 나온다. 여기 함수를 둘 다
  부르고, 데이터는 react-query 가 같은 키를 합쳐 주므로 요청이 늘지 않는다.

  `judge` 는 **판정하는 손**이다 — 감리와 직원(DOC-87 §3-3). 직원은 판정도 하고 요청도 하지만,
  「오늘 답해야 닫히는 것」은 판정 쪽이다 — 요청은 자기가 낸 것이라 답할 것이 아니다.
*/

/** 판정하는 손에게는 요청·검측중이 할 일이고, 협력사는 불합격이 할 일이다(재검을 걸어야 한다). */
export function inspectionTodo(rows: Inspection[], judge: boolean): Inspection[] {
  return judge
    ? rows.filter((row) => row.status === 'REQUESTED' || row.status === 'IN_REVIEW')
    : rows.filter((row) => row.status === 'FAILED');
}

/** 판정하는 손은 「확인할 것」, 협력사는 「고칠 것」. */
export function safetyActionTodo(rounds: SafetyInspection[], judge: boolean): SafetyCorrectiveAction[] {
  const actions = rounds.flatMap((round) => round.actions);
  return actions.filter((row) => (judge ? row.status === '조치완료' : row.status === '지적'));
}

/** 기한을 넘겼는데 아직 지적인 것 — 붉게 세는 값이다. */
export function overdueSafetyActions(rounds: SafetyInspection[]): SafetyCorrectiveAction[] {
  const now = today();
  return rounds.flatMap((round) => round.actions).filter((row) => row.status === '지적' && row.dueDate < now);
}

/** 판정하는 손은 회신을 확인할 것, 협력사는 회신해야 할 것. */
export function ncrTodo(rows: Ncr[], judge: boolean): Ncr[] {
  return judge
    ? rows.filter((row) => row.status === '조치제출' || row.status === '조치확인')
    : rows.filter((row) => row.status === '발행');
}

/** 오늘 TBM. 협력사만 서명한다 — 서명하지 않았으면 그것이 오늘의 첫 할 일이다. */
export function todayTbm(rows: Tbm[]): Tbm | undefined {
  return rows.find((row) => row.tbmDate === today());
}

export function tbmTodoCount(rows: Tbm[]): number {
  const tbm = todayTbm(rows);
  return tbm !== undefined && !tbm.signedByMe ? 1 : 0;
}

/** 판정하는 손은 검토할 것, 협력사는 반려된 것. */
export function drawingTodo(rows: ShopDrawing[], judge: boolean): ShopDrawing[] {
  return judge
    ? rows.filter((row) => row.status === 'SUBMITTED' || row.status === 'RESUBMITTED')
    : rows.filter((row) => row.status === 'REJECTED');
}

/** 판정하는 손에게는 판정 대기(결과값이 적힌 것)가, 협력사에게는 불합격이 할 일이다. */
export function testReportTodo(rows: QualityTestReport[], judge: boolean): QualityTestReport[] {
  return judge
    ? rows.filter((row) => row.verdict === '판정대기' && row.resultValue !== null)
    : rows.filter((row) => row.verdict === '불합격');
}

/** 협력사는 남은 수량, 판정하는 손은 검수 대기. */
export function purchaseTodo(rows: PurchaseOrder[], judge: boolean): PurchaseOrder[] {
  return judge
    ? rows.filter((row) => row.deliveries.some((it) => it.result === '검수대기'))
    : rows.filter((row) => row.deliveredQuantity < row.quantity);
}
